/**
 * The run settings one pool owns, listed where that pool is managed
 * (#2175 slices 3 and 4).
 *
 * **Why one component for two places.** The scheduler's list and a CI
 * connection's list are the same thing pointed at a different pool: the
 * settings this pool may use, what is already using each one, and a way to
 * add another. Only the words around them differ. Two copies would be two
 * places for the row summary to drift, and the summary is exactly the part
 * a reader compares between screens.
 *
 * **Why it loads its own list.** The pool is the component's whole subject,
 * and the request that fetches it is one line. A parent threading the rows
 * through would have to know the pool anyway, and would be the second place
 * that could forget to narrow by it — the mistake slice 1 exists to remove.
 *
 * **Editing here is settings, not a launch.** The dialogs open in their
 * ordinary form, footer and all. The Run / Save and run footer belongs to
 * the manual pool, where the person pressing it is watching.
 */
import { useCallback, useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui'
import { runTemplatesAPI } from '@/lib/api'
import { ReplayTemplateDialog } from '@/components/ReplayTemplateDialog'
import { ResumeTemplateDialog } from '@/components/ResumeTemplateDialog'
import { TestAuthoringTemplateDialog } from '@/components/TestAuthoringTemplateDialog'
import { describePhases } from '@/lib/runPhases'
import type {
  ReplayTemplate,
  RunTemplatePool,
  TestSuite,
  UnifiedRunTemplate,
} from '@/types'

export interface RunTemplatePoolListProps {
  variantId: string
  applicationId: string
  /** Which pool's settings to show. Every read and every create carries it. */
  pool: RunTemplatePool
  /** Section heading, in this pool's own words. */
  heading: string
  /** What to say when the pool is empty — the situation, then what to do. */
  emptyHint: string
  /** How many things in this pool already use a given setting. Returning
   * undefined hides the count for that row. */
  usageCountFor?: (templateId: string) => number | undefined
  /** Singular noun for the count, e.g. "schedule". Pluralized with an s. */
  usageNoun?: string
  /** Optional per-row action, e.g. "Schedule". */
  rowAction?: { label: string; title: string; onClick: (template: UnifiedRunTemplate) => void }
  /** Suites, for the replay editor's test-selection field. */
  suites?: TestSuite[]
  /** Called after a create, an edit or a delete, so a caller showing counts
   * can refresh what it derives them from. */
  onChanged?: () => void
}

/** One line naming what a setting actually does, so a row can be told from
 * its neighbours without opening it. Per kind, because the fields that
 * matter differ; short, because it sits under the name. */
function summarize(t: UnifiedRunTemplate): string {
  const bits: string[] = []
  if (t.kind === 'replay') {
    bits.push(t.suiteId && t.suiteId !== '__all__' ? 'One suite' : 'All tests')
    if (t.browser) bits.push(t.browser)
    if (t.healOnFailure) bits.push('fixes failures')
  } else if (t.kind === 'generation') {
    bits.push('Suggests new tests')
    if (t.maxTestCases) bits.push(`up to ${t.maxTestCases}`)
  } else {
    bits.push('Finishes unfinished work')
    // Was `phaseFilter.join(', ')`, which printed `generate, execute, heal`
    // — code words on a screen an operator reads (#2225).
    const stages = describePhases(t)
    if (stages) bits.push(stages)
  }
  return bits.join(' · ')
}

export function RunTemplatePoolList({
  variantId,
  applicationId,
  pool,
  heading,
  emptyHint,
  usageCountFor,
  usageNoun = 'thing',
  rowAction,
  suites = [],
  onChanged,
}: RunTemplatePoolListProps) {
  const [templates, setTemplates] = useState<UnifiedRunTemplate[]>([])
  const [editing, setEditing] = useState<UnifiedRunTemplate | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      setTemplates(await runTemplatesAPI.listForVariant(variantId, pool) as UnifiedRunTemplate[])
    } catch {
      setTemplates([])
    }
  }, [variantId, pool])
  useEffect(() => { void load() }, [load])

  const afterWrite = () => {
    setEditing(null)
    setCreating(false)
    void load()
    onChanged?.()
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium">{heading}</h3>
        <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          New
        </Button>
      </div>

      {templates.length === 0 ? (
        <p className="text-xs text-muted-foreground rounded-lg border border-dashed px-4 py-3">
          {emptyHint}
        </p>
      ) : (
        templates.map((t) => {
          const used = usageCountFor?.(t.id)
          return (
            <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <button
                type="button"
                className="flex-1 min-w-0 text-left"
                onClick={() => setEditing(t)}
                title="Open these settings"
              >
                <p className="font-medium text-sm truncate">{t.name}</p>
                <p className="text-xs text-muted-foreground">{summarize(t)}</p>
              </button>
              {typeof used === 'number' && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {used === 0
                    ? `No ${usageNoun}s use this yet`
                    : `Used by ${used} ${usageNoun}${used === 1 ? '' : 's'}`}
                </span>
              )}
              {rowAction && (
                <span title={rowAction.title} className="shrink-0">
                  <Button variant="outline" size="sm" onClick={() => rowAction.onClick(t)}>
                    {rowAction.label}
                  </Button>
                </span>
              )}
            </div>
          )
        })
      )}

      {/* New settings are replay-kind. A run on a schedule or a commit is
          replaying the tests that already exist — that is what makes it
          repeatable. The other two kinds still open in their own editor
          when a pool already holds one, so nothing is locked out; there is
          just no reason to offer them as the thing to add here. */}
      {creating && (
        <ReplayTemplateDialog
          template={null}
          variantId={variantId}
          applicationId={applicationId}
          suites={suites}
          pool={pool}
          onClose={() => setCreating(false)}
          onSaved={afterWrite}
        />
      )}

      {editing?.kind === 'replay' && (
        <ReplayTemplateDialog
          template={editing as unknown as ReplayTemplate}
          variantId={variantId}
          applicationId={applicationId}
          suites={suites}
          pool={pool}
          onClose={() => setEditing(null)}
          onSaved={afterWrite}
        />
      )}
      {editing?.kind === 'resume' && (
        <ResumeTemplateDialog
          template={editing}
          variantId={variantId}
          applicationId={applicationId}
          onClose={() => setEditing(null)}
          onSaved={afterWrite}
        />
      )}
      {editing?.kind === 'generation' && (
        <TestAuthoringTemplateDialog
          template={editing}
          allTemplates={[]}
          appId={applicationId}
          variantId={variantId}
          onClose={() => setEditing(null)}
          onSaved={afterWrite}
        />
      )}
    </div>
  )
}
